import React, { useEffect, useRef } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import {
  Button,
  GDSButtonProps,
  HeadingText,
  ImageMessage,
  ParagraphText,
  StackLayout,
  StackItem,
  GDSStyleObject,
} from '@leagueplatform/genesis-core';
import { WEB_COMMON_ASSET_KEYS } from '#types/assets.types.ts';
import {
  SharedErrorStateProps,
  useDisplayIsImpersonatorError,
} from '../../hooks/use-display-is-impersonator-error.hook';

const defaultTryAgainHandler = () => window.location.reload();

export interface ErrorStateProps<TError = unknown>
  extends SharedErrorStateProps<TError> {
  imageSrc?: string;
  bodyString?: React.ReactNode;
  headingString?: string;
  buttonText?: string;
  buttonPriority?: GDSButtonProps<'button'>['priority'];
  hideButton?: boolean;
  hideImage?: boolean;
  onButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  labelledbyId?: string;
  describedbyId?: string;
  emphasisText?: string;
  css?: GDSStyleObject;
}

function ErrorContent<TError>({
  error,
  buttonText,
  bodyString,
  headingString,
  hideButton = false,
  hideImage,
  onButtonClick = defaultTryAgainHandler,
  labelledbyId,
  describedbyId,
  buttonPriority = 'primary',
  emphasisText,
  css,
}: Exclude<ErrorStateProps<TError>, 'imageSrc'>) {
  const { formatMessage } = useIntl();
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    buttonRef?.current?.focus();
  });

  const { isImpersonationSessionType, showImpersonationSessionTypeError } =
    useDisplayIsImpersonatorError({ error });

  let finalHeadingString: string;
  if (showImpersonationSessionTypeError) {
    finalHeadingString = formatMessage(
      { id: 'AGENT_PERMISSION_ERROR' },
      { type: isImpersonationSessionType },
    );
  } else {
    finalHeadingString =
      headingString ||
      formatMessage({ id: 'THERE_SEEMS_T0_HAVE_BEEN_A_PROBLEM' });
  }

  const shouldHideButton = showImpersonationSessionTypeError
    ? true
    : hideButton;

  return (
    <StackLayout
      css={{
        marginTop: !hideImage ? '$three' : 'none',
        textAlign: 'center',
        ...css,
      }}
      horizontalAlignment="center"
      spacing="$twoAndHalf"
    >
      <StackItem className="error-state-text-wrapper">
        <StackLayout horizontalAlignment="center" spacing="$half">
          <HeadingText
            level="2"
            size="lg"
            id={labelledbyId}
            className="error-state-heading-text"
          >
            {finalHeadingString}
          </HeadingText>
          {!showImpersonationSessionTypeError && bodyString && (
            <ParagraphText id={describedbyId} className="error-state-body-text">
              {bodyString}
            </ParagraphText>
          )}
        </StackLayout>
        {emphasisText && (
          <ParagraphText className="error-state-emphasis-text">
            <strong>{emphasisText}</strong>
          </ParagraphText>
        )}
      </StackItem>

      {!shouldHideButton && (
        <StackItem className="error-state-button-wrapper">
          <Button
            onClick={onButtonClick}
            ref={buttonRef}
            priority={buttonPriority}
            className="error-state-button"
          >
            {buttonText || formatMessage({ id: 'TRY_AGAIN' })}
          </Button>
        </StackItem>
      )}
    </StackLayout>
  );
}

export const ErrorState = ({
  hideImage = false,
  imageSrc,
  css,
  ...props
}: ErrorStateProps) => {
  // eslint-disable-next-line react/jsx-props-no-spreading
  if (hideImage) return <ErrorContent css={css} {...props} />;

  const errorStateImage = handleStaticAsset(
    WEB_COMMON_ASSET_KEYS.WEB_COMMON_ERROR_STATE_IMAGE,
  ) as string;
  return (
    <ImageMessage image={imageSrc || errorStateImage}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ErrorContent css={css} {...props} />
    </ImageMessage>
  );
};
